import { registerShellSettingsHotkeys } from './../packages/navigation/hotkeys'
import type { RegisterApplicationConfig } from 'single-spa'
import { registerApplication, start } from 'single-spa'

import { deployedURLs, Environment, isProdEnvironment } from '../environments'
import {
  scaffoldHTML,
  canShowSideBar,
  allowSwitchToDarkmode,
  applyRebranding2021Theme,
  resetChameleonLaunchScreenStyle,
  createGotoSettingsExperience,
  createGeneralSettingsExperiences,
} from './bootstrap-helpers'
import type { ShellAuthContext, ShellConfig, ShellHostConfig } from './models'
import { ShellAuthContextImpl, defaultConfig } from './models'
import { createShellApi } from './shell-api-factory'
import { registerCustomComponents } from './custom-component/components-registration'
import { getShellLoadingTime, SHELL_DONE_MARK, SHELL_START_MARK } from './performance'

import { initializeExternalAdmin } from './hooks/external-admin'
import { initializeCalendar } from './hooks/calendar'
import { initializePendo } from './hooks/pendo'
import { initializeShellController } from './hooks/initialize-shell-controller'
import { initializeMixpanel } from './hooks/mixpanel'
import { initializeContainer } from './hooks/container'
import { initializeI18n } from './hooks/i18n'
import { initializeCommands } from './hooks/launcher-command'
import { initializeDefaultRoute } from './hooks/default-route'
import { initializeContainerQuickAction } from './hooks/container-quick-actions'
import { initializeTopBarVisibility } from './hooks/topbar-visibility'

import { internalExperienceConfigs, viewExperienceConfigs } from '../experiences/index'
import type { ExtensionConfig } from '../extensions/extension-config'
import { getExtensionsManager } from '../extensions/extensions-manager'
import {
  registerExtensionToSingleSpa,
  createExtensionsSettingsInfo,
  getExtensionsSidebarLinks,
} from '../extensions/utils'

import { initializeFavicon } from '../common/favicon'
import type { CustomProps, NavigationLink, SettingsInfo } from '../common/global-props'
import { keepShellStyleSheetLastInNodeList } from '../common/mutation-observer'
import { setTitleElementFromBranding } from '../common/page-title'
import { LANDING_ROUTE } from '../common/routes'
import { getMountedExperiencesFromStatusEvent, startSingleSpaAppChangeLogger } from '../common/single-spa-debug'
import { SINGLE_SPA_BEFORE_MOUNT_EVENT, assertIsSingleSpaCustomEvent } from '../common/single-spa-events'
import { getActiveUrlPath, getLocationHREF, isInIframe, setLocationHref } from '../common/dom-helpers'
import { GOTO_SHELL_VERSION } from '../common/version'
import { getShellLogger } from '../common/logger'

import { setupAppUpdateTimer } from '../services/app-update/app-update-timer'
import { postMessageFromInsideIframe } from '../services/auth/serverIsLoggedIn/isLoggedIn-iframe'
import { getPluginFunction } from '../services/container/helpers'
import { initContainerEvents } from '../services/container/index'
import { isMainWindow } from '../services/container/windows-management'
import { setupAnonymousLaunchDarklyFlags, setupLaunchDarklyFlags } from '../services/feature-flags/index'

import { analytics } from '../services/analytics'
import { setHostAppVersion } from '../common/host-app-version'
import { setUnguardedRoutes } from '../services/auth/authentication'
import { getAuthContextFromApi, getAuthContextFromShellApi, getAuthContextIsUserInternal } from './helpers/auth-context'
import { findAppByName } from './helpers/registered-app'
import { getShellApiInstance } from '../common/shell-api-helpers'
import { getAccountFromContext } from '../common/shell-api-helpers/context'
import { initializeTrialBanner } from './hooks/trial-banner'
import { initRoutingEventListener } from './hooks/routing-event-listener'
import { initializeNotificationController } from './hooks/notification-controller'

const ANALYTICS_APP_LOADING_EVENT = 'GoTo > Load'

export default class Shell {
  private readonly internalApplications = new Map<string, RegisterApplicationConfig<CustomProps>>([
    [
      internalExperienceConfigs.gotoBannerNotificationsConfig.name,
      internalExperienceConfigs.gotoBannerNotificationsConfig,
    ],
    [
      internalExperienceConfigs.gotoSnackbarNotificationsConfig.name,
      internalExperienceConfigs.gotoSnackbarNotificationsConfig,
    ],
    [
      internalExperienceConfigs.gotoModalNotificationsConfig.name,
      internalExperienceConfigs.gotoModalNotificationsConfig,
    ],
  ])

  private navigationOrder: readonly string[] = []
  private readonly unguardedRoutes = new Set<string>()

  private _config: ShellConfig = defaultConfig
  private envConfig: ShellHostConfig | null = null
  private authContext: ShellAuthContext = new ShellAuthContextImpl()

  getNavigationOrder() {
    return this.navigationOrder
  }

  get config() {
    return this._config
  }
  /* istanbul ignore next */
  set config(config) {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  constructor() {
    registerCustomComponents()
    scaffoldHTML()
  }

  getAuthContext() {
    return this.authContext
  }

  /**
   * Add the extensions configuration to the shell object
   * @param config Array of extension config
   * @returns the Shell instance
   */
  addExtensions(extensionConfigs: readonly ExtensionConfig[]): this {
    getExtensionsManager().addExtensions(extensionConfigs)
    return this
  }

  /**
   * Set the expected orders of navigation items. Must be set before starting Shell.
   * @param orderedIds list of navigation items identifiers in expected order
   * @returns the Shell instance
   */
  setNavigationOrder(orderedIds: string[]): this {
    this.navigationOrder = orderedIds
    return this
  }

  /**
   * Get the list of unguarded routes for the application
   */
  private defineUnguardedRoutes() {
    this.unguardedRoutes.add(LANDING_ROUTE)
    const unguardedRoutes = getExtensionsManager().getUnguardedRoutes()
    unguardedRoutes.forEach(unguardedRoute => this.unguardedRoutes.add(unguardedRoute))
  }

  private sortNavigationLinks(items: NavigationLink[]): NavigationLink[] {
    const unknownItemIndex = this.navigationOrder.length
    const compareItems = (item1: NavigationLink, item2: NavigationLink): number => {
      let index1 = this.navigationOrder.indexOf(item1.id)
      index1 = index1 === -1 ? unknownItemIndex : index1
      let index2 = this.navigationOrder.indexOf(item2.id)
      index2 = index2 === -1 ? unknownItemIndex : index2
      return index1 - index2
    }
    return items.length > 1 ? items.sort(compareItems) : items
  }

  private async initializeApplications() {
    await getExtensionsManager().loadExtensions()
    await getExtensionsManager().initializeExtensions()

    const links = this.sortNavigationLinks([...getExtensionsSidebarLinks(getExtensionsManager().getExtensions())])
    this.internalApplications.set(internalExperienceConfigs.gotoNavigationConfig.name, {
      ...internalExperienceConfigs.gotoNavigationConfig,
      activeWhen: () => canShowSideBar(this.config),
      customProps: { links } as CustomProps,
    })
  }

  private async doBeforeAuthentication() {
    /**
     * We set this environment config so that all environment variables are
     * available before any services are spun up.
     */
    if (this.envConfig) {
      globalThis.shellEnvironmentConfig = this.envConfig
    }

    // Add the unguarded routes before authentification
    this.defineUnguardedRoutes()

    if (isMainWindow()) {
      const minuteInMS = 1000 * 60
      let minutes = 0
      getShellLogger().setContext('shell-running-time', minutes)
      setInterval(() => {
        getShellLogger().setContext('shell-running-time', ++minutes)
      }, minuteInMS)
    } else {
      // should use everything from the main window
      return
    }

    if (isInIframe()) {
      postMessageFromInsideIframe()
      throw new Error('Iframe exception')
    }

    getShellLogger().log(`shell version ${GOTO_SHELL_VERSION}`)

    getPluginFunction('Logger', 'setContext')?.('shell_version', GOTO_SHELL_VERSION)

    // Fetch the environment specific flags, must be before authentification
    await setupAnonymousLaunchDarklyFlags()
  }

  private async doAuthentication() {
    await this.doBeforeAuthentication()
    await this.setUnguardedRoutes()
    if (!isMainWindow()) {
      // should use everything from the main window
      this.authContext = getAuthContextFromShellApi(globalThis.opener.shell)
      return
    }

    this.authContext = await getAuthContextFromApi()
    await setupLaunchDarklyFlags({
      numberOfAccounts: this.authContext.accountKeys?.length ?? 0,
      userName: this.authContext.userProfile?.name,
      externalUserKey: this.authContext.userProfile?.id,
      accountKey: this.authContext.contextApi?.account?.key,
      pbxId: this.authContext.pbxId,
      entitlements: this.authContext.user.entitlements,
      licenses: this.authContext.licenses,
      allAccountKeys: this.authContext.accountKeys ?? [],
    })
    this.doPostAuthentication()
  }

  private doPostAuthentication() {
    getShellLogger().setContext(
      'shell_userType',
      getAuthContextIsUserInternal(this.authContext) ? 'internal' : 'external',
    )
  }

  private async doInitializeAPI() {
    if (isMainWindow()) {
      globalThis.shell = Object.freeze(createShellApi(this.authContext))
    } else {
      globalThis.shell = globalThis.opener.shell
    }
    await this.doPostInitializeAPI()
  }

  private shouldRedirectForExtendedRelease(): boolean {
    if (isProdEnvironment()) {
      const accountKey = getAccountFromContext()?.key ?? ''
      const selectedAccount = getShellApiInstance().user.getAccountByKey(accountKey)

      return (selectedAccount?.slowChannelEnabled && !this.isOnExtended()) ?? false
    }
    return false
  }

  private isOnExtended(): boolean {
    return getLocationHREF().includes(deployedURLs.extended_prod)
  }

  private async doPostInitializeAPI() {
    if (this.shouldRedirectForExtendedRelease()) {
      setLocationHref(`https://${deployedURLs.extended_prod}${getActiveUrlPath() ?? ''}`)
    }

    allowSwitchToDarkmode()
    applyRebranding2021Theme()
    resetChameleonLaunchScreenStyle()
    if (isMainWindow()) {
      initContainerEvents()
    }

    await initializeShellController(this)
    await initializeNotificationController(this)
    await initializeContainer(this)
    await initializeCommands(this)
    await initializeTopBarVisibility(this)
    if (isMainWindow()) {
      await initializeExternalAdmin(this)
      await initializeCalendar(this)
      initRoutingEventListener()
    }
    await initializeMixpanel(this)
    // Do not wait, since it involves loading JS code not required for app load
    initializePendo(this)

    analytics.track(ANALYTICS_APP_LOADING_EVENT, { Event: 'Began loading' })

    if (isMainWindow()) {
      await initializeI18n(this)
    }

    try {
      /**
       * These functions can throw, which goto-app interprets as a fatal exception that prevented shell from starting
       * (see https://jira.ops.expertcity.com/browse/SCORE-1753), and does a redirect to the generic error page.
       * However an exception here is not fatal since it's thrown after single spa start.
       *
       * This is a band aid until we can have something that allows us to separately handle errors
       * thrown by non-essential functions.
       */

      initializeTrialBanner()
      keepShellStyleSheetLastInNodeList()
      initializeFavicon()
      setTitleElementFromBranding()
      if (isMainWindow()) {
        setupAppUpdateTimer()
      }
    } catch (e) {
      getShellLogger().error('shell threw a non-fatal exception when starting ', e)
    }
  }

  private async doInitializeShellApplications() {
    await this.initializeApplications()
    await this.doPostInitializeShellApplications()
  }

  private async doPostInitializeShellApplications() {
    if (isMainWindow()) {
      await initializeDefaultRoute(this)
      initializeContainerQuickAction(this)
    }
  }

  private async setUnguardedRoutes() {
    await setUnguardedRoutes(Array.from(this.unguardedRoutes))
  }

  /**
   * Bootstrap single-spa
   *
   * @param config the environment config to run shell in
   *
   */
  async start(config: ShellHostConfig = { environment: Environment.ED, hostAppVersion: GOTO_SHELL_VERSION }) {
    try {
      performance.mark(SHELL_START_MARK)
      this.envConfig = config

      await this.doAuthentication()
      await this.doInitializeAPI()
      await this.doInitializeShellApplications()

      this.registerToSingleSpa()
      this.updateShellConfig()

      setHostAppVersion(config.hostAppVersion)

      getShellLogger().log('single-spa start')

      if (isMainWindow()) {
        registerShellSettingsHotkeys()
      }

      analytics.track(ANALYTICS_APP_LOADING_EVENT, { Event: 'Initialize single SPA' })

      start({
        urlRerouteOnly: true,
      })

      performance.mark(SHELL_DONE_MARK)

      analytics.track(ANALYTICS_APP_LOADING_EVENT, {
        Event: 'Finished loading',
        LoadingTime: getShellLoadingTime(),
      })
    } catch (e: any) {
      if (e.message === 'Iframe exception') {
        return
      }
      getShellLogger().error('Unexpected error', e)
      throw e
    }
  }

  private registerToSingleSpa() {
    const settingsInfos: SettingsInfo[] = []

    const extensions = getExtensionsManager().getExtensions()
    extensions.forEach(extension => registerExtensionToSingleSpa(extension))
    const extensionsSettingsInfo: readonly SettingsInfo[] = createExtensionsSettingsInfo(extensions)
    settingsInfos.push(...extensionsSettingsInfo)

    // Shell main settings application (container)
    const gotoSettingsApplication = createGotoSettingsExperience(settingsInfos)
    // General settings pages
    const generalSettingsApplications = createGeneralSettingsExperiences()
    // Specific Shell views (can be considered as internal Shell applications)
    const viewsApplications = Object.values(viewExperienceConfigs)

    this.internalApplications.set(gotoSettingsApplication.name, gotoSettingsApplication)
    generalSettingsApplications.forEach(generalSettingsApplication =>
      this.internalApplications.set(generalSettingsApplication.name, generalSettingsApplication),
    )
    viewsApplications.forEach(viewsApplication =>
      this.internalApplications.set(viewsApplication.name, viewsApplication),
    )

    // Internal Shell experiences
    const internalApplications: RegisterApplicationConfig<CustomProps>[] = Array.from(
      this.internalApplications.values(),
    )

    internalApplications.forEach(application => {
      registerApplication(application)
    })
    startSingleSpaAppChangeLogger()
  }

  private updateShellConfig() {
    /**
     * Make sure that we are automatically updating the shell config based on the experiences that we are about to mount
     */
    window.addEventListener(SINGLE_SPA_BEFORE_MOUNT_EVENT, event => {
      assertIsSingleSpaCustomEvent(event)
      const mountedExperiences = getMountedExperiencesFromStatusEvent(event)
      this.configure(mountedExperiences)
    })
  }

  /**
   * The configure function is meant to set the config object and then it calls the single spa checkActivityFunctions, this then
   * calls the activeWhen of each app and the UI is updated as expected
   *
   * @param event single SPA before-mount-routing-event event object
   *
   */
  private configure(mountedApps: ReturnType<typeof getMountedExperiencesFromStatusEvent>) {
    this.config = mountedApps
      .filter(appMounted => appMounted !== 'goto/navigation')
      .map(appName =>
        findAppByName(appName, getExtensionsManager().getRegisteredApplications(), this.internalApplications),
      )
      .reduce(
        (config, application) => ({
          ...config,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...((application?.customProps as any)?.shellConfig ?? {}),
        }),
        this.config,
      )
  }
}
